import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PushBanner } from "@/components/avvisi/PushBanner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, Paperclip, Link as LinkIcon, Check, ClipboardList, Compass, Archive, ArchiveRestore, ExternalLink } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PushEnableButton from "@/components/PushEnableButton";
import { isPushSupported, pushStatus } from "@/lib/push";
import { useAuth } from "@/lib/auth";

export default function Bacheca() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState("active");
  const [pushActive, setPushActive] = useState(true); // ottimistico

  React.useEffect(() => {
    if (!isPushSupported()) { setPushActive(false); return; }
    pushStatus().then((s) => setPushActive(s.server && s.local));
  }, []);

  const { data: avvisi = [] } = useQuery({
    queryKey: ["avvisi-mie", tab],
    queryFn: async () => (await api.get("/avvisi/mie", { params: { archived: tab === "archived" } })).data,
  });

  const readMut = useMutation({
    mutationFn: async (id) => (await api.post(`/avvisi/${id}/read`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["avvisi-mie"] });
      qc.invalidateQueries({ queryKey: ["avvisi-unread"] });
    },
  });

  const archiveMut = useMutation({
    mutationFn: async ({ id, restore }) => (await api.post(`/avvisi/${id}/${restore ? "unarchive-me" : "archive-me"}`)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["avvisi-mie"] });
      qc.invalidateQueries({ queryKey: ["avvisi-unread"] });
      toast.success(vars.restore ? "Avviso ripristinato" : "Avviso archiviato");
    },
  });

  const unread = tab === "active" ? avvisi.filter((a) => !a.read) : [];
  const read = tab === "active" ? avvisi.filter((a) => a.read) : avvisi;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[900px] mx-auto">
      <PushBanner/>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-stone-900 flex items-center gap-2">
            <BellRing size={26} className={unread.length > 0 ? "text-red-600" : "text-stone-400"}/> Bacheca avvisi
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {tab === "archived"
              ? `Archivio · ${avvisi.length} avvisi`
              : (unread.length > 0 ? `${unread.length} da leggere · ${read.length} già letti` : `Tutto letto · ${read.length} avvisi totali`)}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white">
            <button onClick={() => setTab("active")} data-testid="portale-tab-attivi" className={`px-3 py-1 text-xs rounded-full transition ${tab === "active" ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>Attivi</button>
            <button onClick={() => setTab("archived")} data-testid="portale-tab-archivio" className={`px-3 py-1 text-xs rounded-full transition ${tab === "archived" ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>Archivio</button>
          </div>
          {user?.track_type !== "modulare" && <Link to="/portale/turni"><Button variant="outline" size="sm" className="text-xs"><ClipboardList size={12} className="mr-1"/> I miei turni</Button></Link>}
          <Link to="/portale/piano"><Button variant="outline" size="sm" className="text-xs"><Compass size={12} className="mr-1"/> Calendario</Button></Link>
        </div>
      </div>

      {!pushActive && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3" data-testid="push-banner">
          <BellRing size={18} className="text-amber-700 flex-shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-900">Attiva le notifiche per non perdere nessun avviso</div>
            <div className="text-xs text-amber-800 mt-0.5">Riceverai un avviso sul telefono quando la segreteria pubblica una nuova comunicazione.</div>
          </div>
          <PushEnableButton/>
        </div>
      )}

      {avvisi.length === 0 && (
        <Card className="p-12 text-center border border-stone-200 rounded-xl shadow-none">
          <Bell size={40} className="text-stone-300 mx-auto mb-2"/>
          <div className="text-stone-400 text-sm">{tab === "archived" ? "Nessun avviso archiviato." : "Nessun avviso in bacheca."}</div>
        </Card>
      )}

      {tab === "active" && unread.length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-medium mb-2">Da leggere</div>
          <div className="space-y-3">
            {unread.map((a) => <AvvisoCard key={a.id} a={a} onRead={() => readMut.mutate(a.id)} onArchive={() => archiveMut.mutate({ id: a.id, restore: false })} isRead={false} archivedView={false}/>)}
          </div>
        </div>
      )}

      {read.length > 0 && (
        <div>
          {tab === "active" && <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-2">Già letti</div>}
          <div className={`space-y-3 ${tab === "active" ? "opacity-70" : ""}`}>
            {read.map((a) => (
              <AvvisoCard
                key={a.id}
                a={a}
                isRead={true}
                archivedView={tab === "archived"}
                onArchive={() => archiveMut.mutate({ id: a.id, restore: tab === "archived" })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AvvisoCard({ a, onRead, onArchive, isRead, archivedView }) {
  const isUrgent = a.priority === "urgent";
  return (
    <Card className={`p-4 border rounded-xl shadow-none ${isUrgent && !isRead ? "border-red-400 bg-red-50/40" : isRead ? "border-stone-200" : "border-emerald-300 bg-emerald-50/40"}`} data-testid={`portale-avviso-${a.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isUrgent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-medium">URGENTE</span>}
            {!isRead && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-medium">NUOVO</span>}
            {archivedView && <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 font-medium">ARCHIVIATO</span>}
            {a.auto && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-medium" data-testid={`avviso-auto-badge-${a.id}`}>AUTOMATICO · {a.auto_kind === "material" ? "MATERIALE" : a.auto_kind === "exercise_reminder" ? "PROMEMORIA" : "ESERCIZIO"}</span>}
            <h3 className={`font-semibold ${isRead ? "text-stone-700" : "text-stone-900"}`}>{a.title}</h3>
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            {fmtDate(a.created_at)} · {a.auto ? `${a.course_label || "Corso"}: ${a.course_name || ""}${a.created_by_name && a.created_by_name !== "Sistema" ? ` · ${a.created_by_name}` : ""}` : `da ${a.created_by_name || "Segreteria"}`}
          </div>
        </div>
        {onArchive && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onArchive} title={archivedView ? "Ripristina" : "Archivia"} data-testid={`portale-arch-${a.id}`}>
            {archivedView ? <ArchiveRestore size={13} className="text-emerald-700"/> : <Archive size={13} className="text-stone-500"/>}
          </Button>
        )}
      </div>
      <div className={`text-sm whitespace-pre-wrap ${isRead ? "text-stone-600" : "text-stone-800"}`}>{a.body}</div>
      {a.auto && a.link && (
        <div className="mt-3">
          <Link to={a.link} onClick={() => !isRead && onRead?.()} data-testid={`avviso-open-${a.id}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#1C352D] text-white text-xs hover:opacity-90 transition-opacity">
            <ExternalLink size={11}/> {a.auto_kind === "material" ? "Vai al materiale" : "Vai all'esercizio"}
          </Link>
        </div>
      )}
      {!a.auto && a.attachments?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {a.attachments.map((att, i) => (
            <a key={i} href={att.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-stone-200 text-xs hover:bg-stone-50" data-testid={`avviso-att-${a.id}-${i}`}>
              {att.kind === "link" ? <LinkIcon size={11}/> : <Paperclip size={11}/>}
              {att.name}
            </a>
          ))}
        </div>
      )}
      {!isRead && !archivedView && (
        <div className="mt-3 pt-3 border-t border-stone-100">
          <Button
            data-testid={`avviso-mark-read-${a.id}`}
            onClick={onRead}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-8 text-xs"
          >
            <Check size={12} className="mr-1"/> Presa visione
          </Button>
        </div>
      )}
    </Card>
  );
}
